import React, {
  useState, useEffect, useRef, useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import _ from 'lodash';
import { useOutletContext } from 'react-router';
import { DateObject } from 'react-multi-date-picker';
import ReactApexChart from 'react-apexcharts';
import { Skeleton } from '@mui/material';
import { useApiData } from '../../../config/reducer/Modules';
import { APIGetDevices, APIGetV3FleetSummary } from '../../../config/restAPI/FetchAPI';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import DateRangePicker from '../../../component/dateRangePicker/DateRangePicker';
import Autocomplete from '../../../component/autocomplete/Autocomplete';
import Card from '../../../component/card/Card';
import './FleetDashboard.css';
import '../FuelAnalytics/AnalyticsDashboard.css';

const LEADERBOARD_SKELETON_ROW_KEYS = [
  'leaderboard-skeleton-1',
  'leaderboard-skeleton-2',
  'leaderboard-skeleton-3',
  'leaderboard-skeleton-4',
  'leaderboard-skeleton-5',
  'leaderboard-skeleton-6',
];

const KpiCardSkeleton = () => (
  <div
    className="styleCard"
    style={{
      width: '100%',
      height: '190px',
      boxSizing: 'border-box',
    }}
  >
    <div className="styleCardContent">
      <Skeleton variant="text" width="45%" height={24} />
      <div className="styleDescription">
        <div className="styleCardBody">
          <Skeleton variant="text" width={140} height={56} />
          <Skeleton variant="text" width="60%" height={20} />
        </div>
      </div>
    </div>
  </div>
);

const ChartCardSkeleton = ({ titleWidth = '50%', showSubtitle = false }) => (
  <div className="fleet-dashboard-chart-card">
    <div className="fleet-dashboard-chart-header">
      <div className="fleet-dashboard-chart-header-text">
        <Skeleton variant="text" width={titleWidth} height={28} />
        {showSubtitle && <Skeleton variant="text" width="30%" height={20} />}
      </div>
    </div>
    <div className="fleet-dashboard-chart">
      <Skeleton variant="rectangular" height={350} sx={{ borderRadius: '8px' }} />
    </div>
  </div>
);

const LeaderboardSkeleton = () => (
  <div className="fleet-dashboard-leaderboard-section">
    <div className="fleet-dashboard-leaderboard-card">
      <Skeleton variant="text" width="45%" height={28} sx={{ mb: 2 }} />
      <div className="fleet-dashboard-leaderboard-table-container">
        <table className="fleet-dashboard-leaderboard-table">
          <thead>
            <tr>
              <th>Asset ID</th>
              <th>Total Harsh Events</th>
              <th>Eco Score</th>
            </tr>
          </thead>
          <tbody>
            {LEADERBOARD_SKELETON_ROW_KEYS.map((rowKey) => (
              <tr key={rowKey}>
                <td aria-label="Loading asset ID"><Skeleton variant="text" width="70%" /></td>
                <td aria-label="Loading harsh events"><Skeleton variant="text" width="40%" /></td>
                <td aria-label="Loading eco score"><Skeleton variant="text" width="30%" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const FleetDashboard = () => {
  const { state } = useLocation();
  const token = Cookies.get('jwtToken');
  const agencyID = Cookies.get('agencyUUID');
  const userTimezone = Cookies.get('timezone');
  const { apiResponse } = useApiData();
  const [loadAsset, setLoadAsset] = useState(true); // for getSearchAsset loader
  const [navigationClick, setNavigationClick, , setNavigationState] = useOutletContext();
  const [listAsset, setListAsset] = useState([]); // this is for list search asset dropdown
  const [assetSelectionList, setAssetSelectionList] = useState({}); // this for asset selection state
  const [inputSelectonText, setInputSelectonText] = useState('');
  const [loadFleet, setLoadFleet] = useState(true);
  const [date, setDate] = useState([]);
  const [inputValue, setInputValue] = useState('Select Dates');
  const [searchTimeout, setSearchTimeout] = useState(null);// buffer when do search
  const [selectedOption, setSelectedOption] = useState(null);
  const uuid = state?.uuid || '';
  const [dateRange, setDateRange] = useState('');
  const [dropDownLoading, setDropDownLoading] = useState(false);
  const [minDateRange, setMinDateRange] = useState(new DateObject().subtract(1, 'year'));
  const [maxDateRange, setMaxDateRange] = useState(new Date());
  const [leaderboardSort, setLeaderboardSort] = useState({ key: 'assetId', direction: 'asc' });
  const fleetRequestRef = useRef(0);
  const lastFleetRequestKey = useRef('');
  const requestAssetsKey = useMemo(
    () => listAsset.map((asset) => asset?.id).filter(Boolean).join('|'),
    [listAsset],
  );
  const formatPickerDate = (value) => moment(new Date(value)).format('MMM D, YYYY');

  // Fleet Dashboard Data
  const [fleetData, setFleetData] = useState({
    kpis: {
      avgEcoScore: { value: 0, change: 0, isPositive: true },
      criticalAlerts: { value: 0, change: 0, isPositive: true },
      assetsOffline: { value: 0, total: 0 },
      onTimePerformance: { value: 0.0, change: 0, isPositive: true },
    },
    ecoScoreChart: [],
    dbaRiskProfile: [],
    vcmAlertSummary: [],
    driverLeaderboard: [],
  });

  const comparisonDays = useMemo(() => {
    if (date.length < 2) return 7;
    const startDate = moment(date[0]?.toDate?.() || date[0]).startOf('day');
    const endDate = moment(date[1]?.toDate?.() || date[1]).startOf('day');
    if (!startDate.isValid() || !endDate.isValid()) return 7;
    return Math.abs(endDate.diff(startDate, 'days')) + 1;
  }, [date]);

const assetPerformanceNavigationList = [
  {
      text: 'Executive Summary and Fleet Health ', value: 1, path: '/analytics/fleetDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.fleetDashboard,
},
  {
      text: 'Fuel Management System', value: 2, path: '/analytics/analyticsDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.analyticsDashboard,
  },
  {
      text: 'VCM & Maintenance Management', value: 3, path: '/analytics/batteryDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.batteryDashboard,
  },
];

const APICalledSearchAssetNGroup = async (inputText = '') => {
  if (!token || !agencyID) {
      setListAsset([]);
      setLoadAsset(false);
      return;
  }
  const params = {
      pageNumber: 1,
      totalPerPage: 100,
      searchKey: inputText,
  };
  try {
      const result = await APIGetDevices(setLoadAsset, token, agencyID, params);
      const isSuccess = result?.data?.status === 200 || result?.status === 200;
      if (isSuccess) {
          const data = result?.data?.data || result?.data || [];
          const asset = data && data.length > 0 ? data.map((x) => ({
              uuid: x.oid,
              id: x.assetName,
              name: x.deviceSerial ? x.deviceSerial : '',
              pairId: x.oid,
              pairedAssets: [],
              fuelSource: x.fuelSource,
              mileageSource: x.mileageSource,
          })) : [];
          const sortCompare = asset.length > 0 ? _.sortBy(asset, 'name') : [];
          setListAsset(sortCompare);
          if (state?.uuid) {
              setSelectedOption(asset.findIndex((e) => e.uuid === uuid));
              setAssetSelectionList(asset.find((e) => e.uuid === uuid));
          }
          setLoadAsset(false);
      } else {
          setListAsset([]);
          setLoadAsset(false);
      }
  } catch (err) {
      setLoadAsset(false);
  }
};

    // this useEffect for call the searchAsset API called. Interval for 0.5 sec
    useEffect(() => {
      if (searchTimeout) {
          clearTimeout(searchTimeout); // this to clear time out
      }
      setSearchTimeout( // to buffer the search by 0.5 second
          setTimeout(() => {
          // APICalledSearchAssetNGroup(); // call the api
          APICalledSearchAssetNGroup(inputSelectonText);
      }, 500),
      );
      return () => clearTimeout(searchTimeout); // this also to clear time out
  }, [inputSelectonText]);

  // this useEffect for onPageLoad
  useEffect(() => {
      if (token) {
          APICalledSearchAssetNGroup();
      }
      // Default to last 7 days on first load
      const defaultStart = new DateObject().subtract(6, 'days');
      const defaultEnd = new DateObject();
      setDate([defaultStart, defaultEnd]);
      setInputValue(`${formatPickerDate(defaultStart)} - ${formatPickerDate(defaultEnd)}`);
      window.history.replaceState({}, document.title);
      setNavigationClick(1);
      setNavigationState('analytics');
  }, []);

  useEffect(() => {
      APICalledSearchAssetNGroup('');
      setDropDownLoading(false);
  }, [dropDownLoading]);

  // // Mock chart data (commented out, now using API)
  // useEffect(() => {
  //   const generateChartData = () => {};
  //   generateChartData();
  // }, []);

  const toNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const aggregateByTimestamp = (points, mode = 'sum') => {
    const bucket = new Map();
    points.forEach((point) => {
      const current = bucket.get(point.x) || { total: 0, count: 0 };
      bucket.set(point.x, { total: current.total + point.y, count: current.count + 1 });
    });
    return Array.from(bucket.entries())
      .map(([x, agg]) => ({ x: Number(x), y: mode === 'avg' ? (agg.total / agg.count) : agg.total }))
      .sort((a, b) => a.x - b.x);
  };

  const mapFleetPayload = (data, prefix) => ({
    kpis: {
      avgEcoScore: {
        value: toNumber(data?.avgEcoScore),
        // change: toNumber(data?.avgEcoScore),
        change: toNumber(data?.percentage),
      },
      criticalAlerts: {
        value: toNumber(data?.assets_with_critical_alerts ?? data?.assetsWithCriticalAlerts),
        change: 0,
      },
      assetsOffline: {
        value: toNumber(data?.totalAssetsOffline),
        total: toNumber(data?.totalAssets),
      },
    },
    ecoScoreChart: (data?.dailyScore || []).map((item) => ({
      x: moment(item.date).valueOf(),
      y: toNumber(item.avgEcoScorePerDay),
    })).filter((x) => Number.isFinite(x.x)),
    ecoScoreWeights: (data?.dailyScore || []).map((item) => ({
      score: toNumber(item.avgEcoScorePerDay),
      weight: toNumber(item.dailyGreenDrivingTypeSum),
    })),
    dbaRiskProfile: (data?.incidentBreakdown || []).map((item, idx) => {
      const rawValue = toNumber(item.pie_chart_value);
      return {
        id: `${prefix}-risk-${idx}`,
        name: item.incidentType || 'Unknown',
        value: rawValue <= 1 ? rawValue * 100 : rawValue,
      };
    }),
    vcmAlertSummary: (data?.dailyScore || []).map((item) => ({
      x: moment(item.date).valueOf(),
      y: toNumber(item.dailyGreenDrivingTypeSum),
    })).filter((x) => Number.isFinite(x.x)),
    driverLeaderboard: (data?.assetScore || []).map((item, idx) => ({
      id: `${prefix}-driver-${idx}`,
      assetId: item.assetName || '-',
      avgEcoScore: (() => {
        const rawEcoScore = toNumber(item.avgEcoScorePerAsset);
        const ecoScorePercent = rawEcoScore <= 10 ? rawEcoScore * 10 : rawEcoScore;
        return Number(ecoScorePercent.toFixed(1));
      })(),
      harshEventsPer10Hrs: Math.round(toNumber(item.harshEventsPerTenHoursPerAsset)),
      idlingRatio: (() => {
        const rawRatio = toNumber(item.idlingRatioPerAsset);
        return rawRatio <= 1 ? rawRatio * 100 : rawRatio;
      })(),
    })),
  });

  const fetchFleetSummary = async (fromTime, toTime, selectedAssetName) => {
    if (!token || !agencyID) return;
    fleetRequestRef.current += 1;
    const currentRequestId = fleetRequestRef.current;
    setLoadFleet(true);
    try {
      const allAssetNames = listAsset.map((asset) => asset?.id).filter((name) => Boolean(name));
      const assetNamesParam = selectedAssetName ? [selectedAssetName] : allAssetNames;

      if (assetNamesParam.length === 0) {
        if (currentRequestId !== fleetRequestRef.current) return;
        setLoadFleet(false);
        return;
      }

      const result = await APIGetV3FleetSummary(
        setLoadFleet,
        token,
        agencyID,
        fromTime,
        toTime,
        assetNamesParam,
      );

      let normalizedPayloads = [];
      if (result?.data?.status === 200) {
        const responseData = result?.data?.data;
        normalizedPayloads = Array.isArray(responseData) ? responseData : [responseData || {}];
      } else if (!selectedAssetName) {
        // Fallback to per-asset calls if backend rejects bulk array format.
        const results = await Promise.all(
          allAssetNames.map((assetName) => APIGetV3FleetSummary(
            setLoadFleet,
            token,
            agencyID,
            fromTime,
            toTime,
            assetName,
          )),
        );

        normalizedPayloads = results
          .filter((r) => r?.data?.status === 200)
          .map((r) => (Array.isArray(r?.data?.data) ? (r.data.data[0] || {}) : (r?.data?.data || {})));
      }

      const mapped = normalizedPayloads.map((payload, idx) => mapFleetPayload(payload, `a${idx}`));

      const criticalAlertsValue = Math.max(
        ...normalizedPayloads.map((payload) => toNumber(
          payload?.assets_with_critical_alerts
          ?? payload?.assetsWithCriticalAlerts,
        )),
        0,
      );

      if (mapped.length === 0) {
        if (currentRequestId !== fleetRequestRef.current) return;
        setLoadFleet(false);
        return;
      }

      const riskMap = new Map();
      mapped.flatMap((x) => x.dbaRiskProfile).forEach((risk) => {
        const current = riskMap.get(risk.name) || 0;
        riskMap.set(risk.name, current + risk.value);
      });

      const avgEcoScoreFromApi = selectedAssetName
        ? mapped[0].kpis.avgEcoScore.value
        : (() => {
          const weighted = mapped
            .flatMap((x) => x.ecoScoreWeights)
            .filter((x) => x.weight > 0);
          const totalWeight = weighted.reduce((sum, x) => sum + x.weight, 0);
          if (totalWeight > 0) {
            return weighted.reduce((sum, x) => sum + (x.score * x.weight), 0) / totalWeight;
          }
          return mapped.reduce((sum, x) => sum + x.kpis.avgEcoScore.value, 0) / mapped.length;
        })();

      if (currentRequestId !== fleetRequestRef.current) return;
      setFleetData({
        kpis: {
          avgEcoScore: {
            value: avgEcoScoreFromApi,
            change: mapped.reduce((sum, x) => sum + x.kpis.avgEcoScore.change, 0) / mapped.length,
            isPositive: (mapped.reduce((sum, x) => sum + x.kpis.avgEcoScore.change, 0) / mapped.length) >= 0,
          },
          criticalAlerts: {
            value: criticalAlertsValue,
            change: 0,
            isPositive: true,
          },
          assetsOffline: {
            value: mapped.reduce((sum, x) => sum + x.kpis.assetsOffline.value, 0),
            total: mapped.reduce((sum, x) => sum + x.kpis.assetsOffline.total, 0),
          },
          onTimePerformance: { value: 0, change: 0, isPositive: true },
        },
        ecoScoreChart: aggregateByTimestamp(mapped.flatMap((x) => x.ecoScoreChart), 'avg'),
        dbaRiskProfile: Array.from(riskMap.entries()).map(([name, value], idx) => ({
          id: `risk-${idx}`,
          name,
          value,
          color: ['#F59E0B', '#3B82F6', '#8B5CF6', '#F04438'][idx % 4],
        })),
        vcmAlertSummary: aggregateByTimestamp(mapped.flatMap((x) => x.vcmAlertSummary), 'sum'),
        driverLeaderboard: mapped
          .flatMap((x) => x.driverLeaderboard)
          .sort((a, b) => b.avgEcoScore - a.avgEcoScore),
      });

      setLoadFleet(false);
    } catch (err) {
      if (currentRequestId !== fleetRequestRef.current) return;
      setLoadFleet(false);
    }
  };

  useEffect(() => {
    if (!token || !agencyID || date.length < 2 || loadAsset) return;
    const requestTimezone = userTimezone || moment.tz.guess();
    const fromTime = moment(date[0]?.toDate?.() || date[0]).tz(requestTimezone).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
    const toTime = moment(date[1]?.toDate?.() || date[1]).tz(requestTimezone).endOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
    const assetName = assetSelectionList?.id || null;
    // Asset searches toggle loadAsset even when their resolved result is unchanged.
    const requestKey = `${fromTime}|${toTime}|${assetName || ''}|${requestAssetsKey}`;
    if (lastFleetRequestKey.current === requestKey) return;
    lastFleetRequestKey.current = requestKey;
    fetchFleetSummary(fromTime, toTime, assetName);
  }, [date, assetSelectionList?.id, requestAssetsKey, listAsset.length, loadAsset, agencyID]);

  // If asset search finished with no devices, stop showing the skeleton
  useEffect(() => {
    if (!loadAsset && listAsset.length === 0) {
      setLoadFleet(false);
    }
  }, [loadAsset, listAsset.length]);

  // Chart options for Performance Trending (Eco Score)
  const ecoScoreChartOptions = {
    chart: {
      id: 'eco-score-chart',
      type: 'line',
      toolbar: { show: false },
      fontFamily: 'Inter',
    },
    stroke: {
      curve: 'smooth',
      width: 2,
      colors: ['#101828'],
    },
    markers: {
      size: 4,
      colors: ['#101828'],
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: ['#101828'],
        inverseColors: false,
        opacityFrom: 0.3,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        format: 'MMM d',
      },
    },
    yaxis: {
      // title: {
      //   text: 'Percentage (%)',
      // },
      min: 0,
      max: 10,
      labels: {
        formatter: (value) => `${value}`,
      },
    },
    tooltip: {
      x: {
        format: 'MMM dd, yyyy',
      },
      y: {
        formatter: (value) => `${value.toFixed(2)}`,
      },
    },
    colors: ['#101828'],
    dataLabels: {
      enabled: true,
      formatter: (value) => `${value.toFixed(2)}`,
      offsetY: -10,
    },
  };

  // Chart options for Fleet DBA Risk Profile (Donut)
  const dbaRiskChartOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter',
    },
    labels: fleetData.dbaRiskProfile.length > 0
      ? fleetData.dbaRiskProfile.map((item) => item.name)
      : [],
    colors: fleetData.dbaRiskProfile.length > 0
      ? fleetData.dbaRiskProfile.map((item) => item.color)
      : [],
    legend: {
      position: 'right',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
    },
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(0)}`,
      },
    },
  };

  const vcmAlertChartOptions = useMemo(
    () => ({
      chart: {
        id: 'vcm-alert-chart',
        type: 'bar',
        toolbar: { show: false },
        fontFamily: 'Inter',
      },
      plotOptions: {
        bar: {
          columnWidth: '50%',
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'MMM d, yyyy',
          datetimeUTC: false,
        },
        tickPlacement: 'on',
      },
      yaxis: {
        min: 0,
        title: { text: 'Alert Count' },
        labels: {
          formatter: (val) => `${Math.round(val)}`,
        },
        axisBorder: { show: true },
        axisTicks: { show: true },
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const raw = fleetData.vcmAlertSummary[dataPointIndex];
          if (!raw) return '';
          const alertCount = Math.round(Number(raw.y) || 0);
          const dateStr = moment(raw.x).format('MMM DD, YYYY');
          return `<div style="padding:8px 12px;font-size:12px"><div style="margin-bottom:4px;color:#667085">${dateStr}</div><div style="font-weight:600;color:#101828">Alerts by Category: ${alertCount}</div></div>`;
        },
      },
      colors: ['#101828'],
      dataLabels: {
        enabled: false,
      },
    }),
    [fleetData.vcmAlertSummary],
  );

  const sortedLeaderboard = useMemo(() => {
    const data = [...fleetData.driverLeaderboard];
    const { key, direction } = leaderboardSort;
    const factor = direction === 'asc' ? 1 : -1;

    data.sort((a, b) => {
      if (key === 'assetId') {
        return a.assetId.localeCompare(b.assetId) * factor;
      }

      const aVal = key === 'harshEventsPer10Hrs' ? a.harshEventsPer10Hrs : a.avgEcoScore;
      const bVal = key === 'harshEventsPer10Hrs' ? b.harshEventsPer10Hrs : b.avgEcoScore;

      if (aVal === bVal) return 0;
      return aVal > bVal ? factor : -factor;
    });

    return data;
  }, [fleetData.driverLeaderboard, leaderboardSort]);

  const handleLeaderboardSort = (key) => {
    setLeaderboardSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const leaderboardSortArrowChar = (columnKey) => {
    if (leaderboardSort.key !== columnKey) return '↕';
    return leaderboardSort.direction === 'desc' ? '↓' : '↑';
  };

  const leaderboardSortArrowClass = (columnKey) => (
    leaderboardSort.key === columnKey
      ? 'fleet-table-sort-arrow fleet-table-sort-arrow-active'
      : 'fleet-table-sort-arrow fleet-table-sort-arrow-idle'
  );

  return (
    <div className="fleet-dashboard-container">
      <ListNavigationTab list={assetPerformanceNavigationList} navigtionClick={navigationClick} setNavigtionClick={setNavigationClick} />

      {/* Main Title, Description and Filters in one row */}
      <div className="fleet-dashboard-title-section">
        <div className="fleet-dashboard-title-content">
          <h1 className="fleet-dashboard-main-title">Executive Summary and Fleet Health</h1>
          <p className="fleet-dashboard-description">
          This dashboard delivers a high-level overview of fleet health, presenting real-time insights into operational efficiency, resource utilization, and safety compliance.
          </p>
        </div>
        {/* <div className="fleet-dashboard-title-filters"> */}
        <div className="fleet-analytics-filters">
          {/* <div className="fleet-dashboard-filter"> */}
          <div className="fleet-analytics-filter-box">
            <DateRangePicker
              date={date}
              setDate={setDate}
              inputValue={inputValue}
              setInputValue={setInputValue}
              minDateRange={minDateRange}
              setMinDateRange={setMinDateRange}
              maxDateRange={maxDateRange}
              setMaxDateRange={setMaxDateRange}
              setApplyDatePicker={() => {}}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div>
          <div className="fleet-analytics-filter-box">
            <Autocomplete
              component="FleetDashboard"
              autocompleteComponent="AssetSearch"
              list={listAsset}
              setSelectionList={setAssetSelectionList}
              selectionList={assetSelectionList}
              text="Select asset / device"
              isImage
              isAgencySelector={false}
              inputValue={inputSelectonText}
              stateInputValue={setInputSelectonText}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              dropDownLoading={dropDownLoading}
              setDropDownLoading={setDropDownLoading}
              loadAsset={loadAsset}
            />
          </div>
        </div>
      </div>

      {loadFleet ? (
        <>
          <div className="fleet-dashboard-kpi-section">
            <KpiCardSkeleton />
            <KpiCardSkeleton />
          </div>
          <div className="fleet-dashboard-charts-section">
            <ChartCardSkeleton titleWidth="55%" />
            <ChartCardSkeleton titleWidth="50%" />
          </div>
          <div className="fleet-dashboard-bottom-chart-section">
            <ChartCardSkeleton titleWidth="45%" showSubtitle />
          </div>
          <LeaderboardSkeleton />
        </>
      ) : (
        <>
          {/* KPI Cards Section */}
          <div className="fleet-dashboard-kpi-section">
            <Card
              title="Avg. Eco Score"
              body={`${fleetData.kpis.avgEcoScore.value.toFixed(2)}%`}
              // body={`${fleetData.kpis.avgEcoScore.change.toFixed(0)}%`}
              checkArrow={fleetData.kpis.avgEcoScore.isPositive}
              different={fleetData.kpis.avgEcoScore.change.toFixed(0)}
              comparisonDays={comparisonDays}
              width="100%"
            />
            <Card
              title="Assets with Critical Alerts"
              body={fleetData.kpis.criticalAlerts.value.toLocaleString()}
              checkArrow={fleetData.kpis.criticalAlerts.isPositive}
              different={fleetData.kpis.criticalAlerts.change.toFixed(0)}
              comparisonDays={comparisonDays}
              width="100%"
            />
            {/* <Card
              title="Total Assets Offline"
              body={`${fleetData.kpis.assetsOffline.value} / ${fleetData.kpis.assetsOffline.total}`}
            //   noChange
              width="100%"
            /> */}
          </div>

          {/* Two Charts Section - Side by Side */}
          <div className="fleet-dashboard-charts-section">
            <div className="fleet-dashboard-chart-card">
              <div className="fleet-dashboard-chart-header">
                <h3 className="fleet-dashboard-chart-title">Performance Trending (Eco Score)</h3>
              </div>
              <div className="fleet-dashboard-chart">
                {fleetData.ecoScoreChart.length > 0 && (
                  <ReactApexChart
                    options={ecoScoreChartOptions}
                    series={[{ name: 'Average Eco Score', data: fleetData.ecoScoreChart }]}
                    type="line"
                    height={350}
                  />
                )}
              </div>
            </div>

            {/* Right Chart: Fleet DBA Risk Profile */}
            <div className="fleet-dashboard-chart-card">
              <div className="fleet-dashboard-chart-header">
                <h3 className="fleet-dashboard-chart-title">Fleet DBA Risk Profile</h3>
                {/* <div className="fleet-dashboard-chart-actions">
                  <span>🔔</span>
                  <span>📋</span>
                  <span>☰</span>
                </div> */}
              </div>
              <div className="fleet-dashboard-chart">
                {fleetData.dbaRiskProfile.length > 0 && (
                  <ReactApexChart
                    options={dbaRiskChartOptions}
                    series={fleetData.dbaRiskProfile.map((item) => item.value)}
                    type="donut"
                    height={350}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Bottom Chart: VCM Critical Alert Summary */}
          <div className="fleet-dashboard-bottom-chart-section">
            <div className="fleet-dashboard-chart-card">
              <div className="fleet-dashboard-chart-header">
                <div className="fleet-dashboard-chart-header-text">
                  <h3 className="fleet-dashboard-chart-title">VCM Critical Alert Summary</h3>
                  <p className="fleet-dashboard-chart-subtitle">Alerts by Category</p>
                </div>
                {/* <div className="fleet-dashboard-chart-actions">
                  <span>🔔</span>
                  <span>AZ</span>
                  <span>☰</span>
                  <span>⋮</span>
                </div> */}
              </div>
              <div className="fleet-dashboard-chart">
                {fleetData.vcmAlertSummary.length > 0 && (
                  <ReactApexChart
                    options={vcmAlertChartOptions}
                    series={[{ name: 'Alerts by Category', data: fleetData.vcmAlertSummary }]}
                    type="bar"
                    height={350}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Driver Leaderboard Table */}
          <div className="fleet-dashboard-leaderboard-section">
            <div className="fleet-dashboard-leaderboard-card">
              <h3 className="fleet-dashboard-leaderboard-title">Driver Leaderboard (Ranking by Eco Score)</h3>
              <div className="fleet-dashboard-leaderboard-table-container">
                <table className="fleet-dashboard-leaderboard-table">
                  <thead>
                    <tr>
                      <th>
                        <button
                          type="button"
                          className="fleet-dashboard-table-sortable-header"
                          onClick={() => handleLeaderboardSort('assetId')}
                        >
                          Asset ID
                          <span className={leaderboardSortArrowClass('assetId')}>
                            {leaderboardSortArrowChar('assetId')}
                          </span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="fleet-dashboard-table-sortable-header"
                          onClick={() => handleLeaderboardSort('harshEventsPer10Hrs')}
                        >
                          Total Harsh Events
                          <span className={leaderboardSortArrowClass('harshEventsPer10Hrs')}>
                            {leaderboardSortArrowChar('harshEventsPer10Hrs')}
                          </span>
                        </button>
                      </th>
                      <th>
                        <button
                          type="button"
                          className="fleet-dashboard-table-sortable-header"
                          onClick={() => handleLeaderboardSort('avgEcoScore')}
                        >
                          Eco Score
                          <span className={leaderboardSortArrowClass('avgEcoScore')}>
                            {leaderboardSortArrowChar('avgEcoScore')}
                          </span>
                        </button>
                      </th>
                      {/* <th>Idling Ratio</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLeaderboard.map((row) => (
                      <tr key={row.id}>
                        <td>{row.assetId}</td>
                        <td>{row.harshEventsPer10Hrs}</td>
                        <td>{(row.avgEcoScore / 10).toFixed(1)}</td>
                        {/* <td>
                          {row.idlingRatio.toFixed(1)}
                          %
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// const AnalyticsDashboard = () => (
//   <div className="analytics-dashboard-container">
//     <div className="analytics-dashboard-header">
//       <Title
//         title="Analytics"
//         subtitle="Overview dashboard for asset performance and origin / destination (O/D) analytics"
//         titleSize="1.875rem"
//       />
//     </div>
//     {/* you can add cards / charts here later */}
//   </div>
// );

export default FleetDashboard;
